import { useTranslation } from 'react-i18next';

import { EmptySkeleton } from '@/components/ui/custom/EmptySkeleton';
import { useEmptyStateView } from '@/hooks/useEmptyStateView';
import { useRunsPage } from '@/modules/simulations/hooks/useRunsPage';

import { EmptySimulations } from '../EmptySimulations';
import { RunsTable } from './RunsTable/RunsTable';

interface SimulationsOverviewProps {
  brainId: string | undefined;
}

export const SimulationsOverview = ({ brainId }: SimulationsOverviewProps) => {
  const { t } = useTranslation();
  const page = useRunsPage(brainId);

  const hasActiveFilters = page.activeFilterCount > 0 || !!page.dateRange.startDate;
  const { showEmpty, showEmptySkeleton, showList } = useEmptyStateView({
    scope: `simulation-runs:${brainId}`,
    isLoading: page.isLoading,
    isEmpty: page.runs.length === 0 && !hasActiveFilters,
    isReady: !!brainId,
    isError: page.listStatus === 'error',
  });

  if (showEmpty) {
    return (
      <EmptySimulations
        title={t('simulations.no_runs_yet')}
        description={t('simulations.no_simulations_description')}
      />
    );
  }

  if (showEmptySkeleton) {
    return <EmptySkeleton media="icon" descriptionLines={3} actions={0} />;
  }

  if (!showList) {
    return null;
  }

  return (
    <RunsTable
      data={page.currentPageData}
      rowCount={page.rowCount}
      isLoading={page.isLoading}
      isFetchingNextPage={page.isFetchingNextPage}
      fetchNextPage={page.goToNextPage}
      hasNextPage={page.hasNextPage}
      currentPageIndex={page.currentPageIndex}
      setCurrentPageIndex={page.setCurrentPageIndex}
      sorting={page.sorting}
      onSortingChange={page.setSorting}
      filters={page.filters}
      activeFilterCount={page.activeFilterCount}
      setFilter={page.setFilter}
      removeFilter={page.removeFilter}
      resetFilters={page.resetFilters}
      selection={page.selection}
      onSelectionChange={page.setSelection}
      getCadence={page.getCadence}
      onStop={page.cancelRun}
      onDelete={page.deleteRun}
    />
  );
};
